import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "../../utils/hooks/useAxios";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { filterObject } from "@/utils/helpers";
import { useSystem } from "@/utils/hooks/useLocation";
import { SYSTEMS } from "@/utils/constants";

// ******* Queries *******

export const useGetMakkahGroupsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const system =
    currentSystem === SYSTEMS.makkah ? "makkah" : "madina";

  // Build query parameters
  const queryParams = new URLSearchParams();

  // Add search parameter if provided
  if (params.query && params.query.trim()) {
    queryParams.append("query", params.query.trim());
  }

  // Add page parameter if provided
  if (params.page) {
    queryParams.append("page", params.page);
  }

  // Add other parameters if needed
  Object.keys(params).forEach((key) => {
    if (
      key !== "query" &&
      key !== "page" &&
      params[key] !== undefined &&
      params[key] !== null &&
      params[key] !== ""
    ) {
      queryParams.append(key, params[key]);
    }
  });

  const queryString = queryParams.toString();
  const url = queryString
    ? `${system}/makkah-groups?${queryString}`
    : `${system}/makkah-groups`;

  return useQuery({
    queryKey: ["makkah-groups", params, system],
    queryFn: () => axiosProtected.get(url),
  });
};

/** GET {system}/departure-group — paginated departure groups (groups → Departure tab) */
export const useGetMakkahDepartureGroupsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const system =
    currentSystem === SYSTEMS.makkah ? "makkah" : "madina";
  const filtered = filterObject(params);
  const queryParams = new URLSearchParams();
  Object.keys(filtered).forEach((key) => {
    queryParams.append(key, filtered[key]);
  });
  const queryString = queryParams.toString();
  const url = queryString
    ? `${system}/departure-group?${queryString}`
    : `${system}/departure-group`;

  return useQuery({
    queryKey: ["makkah-departure-group", filtered, system],
    queryFn: () => axiosProtected.get(url),
  });
};

export const useGetOneDepartureGroupQuery = ({ id }) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const system =
    currentSystem === SYSTEMS.makkah ? "makkah" : "madina";

  return useQuery({
    queryKey: ["one-departure-group", id, system],
    queryFn: () => axiosProtected.get(`${system}/departure-group/${id}`),
    enabled: !!id,
  });
};

export const useCreateDepartureGroupSegmentMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();
  const system =
    currentSystem === SYSTEMS.makkah ? "makkah" : "madina";

  return useMutation({
    mutationFn: (payload) =>
      axiosProtected.post(`${system}/departure-group`, payload),
    onSuccess: () => {
      toast.success(t("DEPARTURE_CREATED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["one-departure-group"] });
    },
  });
};

export const useUpdateDepartureGroupSegmentMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();
  const system =
    currentSystem === SYSTEMS.makkah ? "makkah" : "madina";

  return useMutation({
    mutationFn: ({ id, ...payload }) =>
      axiosProtected.post(`${system}/departure-group/${id}`, payload),
    onSuccess: () => {
      toast.success(t("DEPARTURE_CREATED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["one-departure-group"] });
    },
  });
};

export const useDeleteDepartureGroupSegmentMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();
  const system =
    currentSystem === SYSTEMS.makkah ? "makkah" : "madina";

  return useMutation({
    mutationFn: ({ id }) =>
      axiosProtected.delete(`${system}/departure-group/delete/${id}`),
    onSuccess: () => {
      toast.success(t("DELETE_DEPARTURE_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["one-departure-group"] });
    },
  });
};

export const useGetRawGroupNamesQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { mission_id } = params;

  return useQuery({
    queryKey: ["raw-group-names", params],
    queryFn: () => axiosProtected.get("makkah/pilgrim-group-names", {
      params
    }),
    enabled: !!mission_id,
  });
};

/**
 * GET makkah/inquiries/group-pilgrims - paginated list with filters
 */
export const useGetGroupPilgrimsInquiryQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const filteredParams = filterObject(params);
  return useQuery({
    queryKey: ["group-pilgrims-inquiry", filteredParams],
    queryFn: () =>
      axiosProtected.get("makkah/inquiries/group-pilgrims", {
        params: filteredParams,
      }),
  });
};

export const useGetHouseContractsQuery = ({ enabled = false } = {}) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["house-contracts"],
    queryFn: () => axiosProtected.get("makkah/house-contracts"),
    enabled,
  });
};

// ******* Mutations *******

export const useDeleteMakkahGroupMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();
  const system =
    currentSystem === SYSTEMS.makkah ? "makkah" : "madina";

  return useMutation({
    mutationFn: ({ id }) => {
      return axiosProtected.get(`${system}/makkah-groups/delete/${id}`);
    },
    onSuccess: () => {
      toast.success(t("GROUP_ITEM_DELETED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["makkah-groups"] });
    },
    onError: (err) => {
      console.error("Group delete error:", err);
      toast.error(
        err?.response?.data?.message || t("GROUP_ITEM_DELETE_FAILED")
      );
    },
  });
};

export const useImportMakkahDepartureGroupMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();
  const system =
    currentSystem === SYSTEMS.makkah ? "makkah" : "madina";

  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("file", file);

      return axiosProtected.post(`${system}/departure-group/import`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    onSuccess: () => {
      toast.success(t("GROUP_IMPORTED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["makkah-departure-group"] });
    },
    onError: (err) => {
      console.error("Departure group import error:", err);
      toast.error(err?.response?.data?.message || t("GROUP_IMPORT_FAILED"));
    },
  });
};

export const useImportMakkahGroupMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("file", file);

      return axiosProtected.post("makkah/groups-dummy/import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    onSuccess: () => {
      toast.success(t("GROUP_IMPORTED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["makkah-groups"] });
    },
    onError: (err) => {
      console.error("Group import error:", err);
      toast.error(err?.response?.data?.message || t("GROUP_IMPORT_FAILED"));
    },
  });
};
